import React, { useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import fileIcon from '../../assets/file.svg';
import downloadIcon from '../../assets/Download.svg';
import viewAssetIcon from '../../assets/Eye.svg';
import downloadAssetIcon from '../../assets/Download-grey.svg';
import api from '../../api';
import { notify } from '../CustomToast';
import './UploadedDocumentsModal.css';
import TaskAttachmentPdfViewerModal from './TaskAttachmentPdfViewerModal';
import TaskAttachmentImageViewerModal from './TaskAttachmentImageViewerModal';
import TaskAttachmentOnlyOfficeViewerModal from './TaskAttachmentOnlyOfficeViewerModal';
import { isPdfFile } from '../FileManager/PdfViewerModal';
import { isImageFile } from '../FileManager/ImageViewerModal';

const OFFICE_EXTENSIONS = ['doc', 'docx', 'odt', 'rtf', 'xls', 'xlsx', 'ods', 'csv', 'ppt', 'pptx', 'odp'];
const isOfficeFile = (file) => {
    const ext = (file?.original_filename || file?.name || '').split('.').pop()?.toLowerCase();
    return OFFICE_EXTENSIONS.includes(ext);
};

// Helper to get color based on file extension
const getFileIconColor = (ext) => {
    const colors = {
        'PDF': '#dc2626', // Red
        'DOC': '#2b579a', 'DOCX': '#2b579a', // Blue
        'XLS': '#217346', 'XLSX': '#217346', 'CSV': '#217346', // Green
        'JPG': '#e67e22', 'JPEG': '#e67e22', 'PNG': '#e67e22', 'SVG': '#e67e22', 'GIF': '#e67e22', // Orange
        'TXT': '#7f8c8d', 'RTF': '#7f8c8d', // Grey
        'ZIP': '#8e44ad', 'RAR': '#8e44ad', '7Z': '#8e44ad', // Purple
    };
    return colors[ext] || '#0c5097'; // Default theme blue
};

// Custom File Icon matching the design
const FileIconBadge = ({ ext = 'PDF' }) => (
    <div className="file-type-icon">
        <div className="file-icon-base">
            <div className="file-icon-lines">
                <div className="file-icon-line"></div>
                <div className="file-icon-line"></div>
                <div className="file-icon-line"></div>
            </div>
            <span
                className="file-icon-pdf-badge"
                style={{ backgroundColor: getFileIconColor(ext) }}
            >
                {ext}
            </span>
        </div>
    </div>
);

// Download All Icon (with tray)
const DownloadAllIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill="currentColor" />
    </svg>
);

const SkeletonRow = () => (
    <div className="skeleton-item">
        <div className="skeleton-item-left">
            <div className="skeleton-block skeleton-icon" />
            <div className="skeleton-text-group">
                <div className="skeleton-block skeleton-name" />
                <div className="skeleton-block skeleton-date" />
            </div>
        </div>
        <div className="skeleton-actions">
            <div className="skeleton-block skeleton-btn" />
            <div className="skeleton-block skeleton-btn" />
        </div>
    </div>
);

const UploadedDocumentsModal = ({ isOpen, onClose, task, attachments: initialAttachments = [], loading = false, onUpload, onDownload, onDownloadAll, onView }) => {
    const [attachments, setAttachments] = React.useState(initialAttachments);
    const [uploadingFiles, setUploadingFiles] = React.useState([]);
    const [isDownloadingAll, setIsDownloadingAll] = React.useState(false);
    const fileInputRef = React.useRef(null);
    const [officeAttachment, setOfficeAttachment] = React.useState(null);
    const [imageAttachment, setImageAttachment] = React.useState(null);
    const [pdfAttachment, setPdfAttachment] = React.useState(null);

    const imageAttachments = useMemo(
        () => attachments.filter(a => isImageFile(a.upload || {})),
        [attachments]
    );

    React.useEffect(() => {
        setAttachments(initialAttachments);
    }, [initialAttachments]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${month}-${day}-${year} ${hours}:${minutes} ${ampm}`;
    };

    const getFileExtension = (fileName) => {
        if (!fileName) return 'FILE';
        const ext = fileName.split('.').pop()?.toUpperCase();
        return ext || 'FILE';
    };

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) { // 50MB limit
            notify('error', 'File too large', 'Max file size is 50MB');
            return;
        }

        const uploadId = Date.now(); // Local temporary ID for tracking
        const newUploadingFile = {
            id: uploadId,
            file_name: file.name,
            progress: 0,
            isUploading: true,
            created_at: new Date().toISOString()
        };

        setUploadingFiles(prev => [newUploadingFile, ...prev]);

        try {
            // 1. Upload to temp-upload
            const formData = new FormData();
            formData.append('file', file);

            const tempUploadResponse = await api.post('/temp-upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = progressEvent.total
                        ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        : 0;
                    setUploadingFiles(prev => prev.map(f =>
                        f.id === uploadId ? { ...f, progress: percentCompleted } : f
                    ));
                }
            });

            if (tempUploadResponse.status) {
                const tempUploadId = tempUploadResponse.data.id;

                // 2. Attach to task
                const attachResponse = await api.post(`/v1/tasks/${task.id}/attachments`, {
                    upload_id: tempUploadId
                });

                if (attachResponse.status) {
                    const newAttachment = attachResponse.data.attachment;
                    setAttachments(prev => [newAttachment, ...prev]);
                    if (onUpload) {
                        onUpload(newAttachment);
                    }
                    notify('success', 'File uploaded successfully');
                }
            }
        } catch (error) {
            console.error('Upload failed:', error);
            notify('error', 'Upload failed', error.message || 'Something went wrong');
        } finally {
            setUploadingFiles(prev => prev.filter(f => f.id !== uploadId));
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    return (
        <>
        <div className="uploaded-docs-modal-overlay" onClick={handleBackdropClick}>
            <div className="uploaded-docs-modal">
                {/* Header */}
                <div className="uploaded-docs-modal-header">
                    <h2 className="uploaded-docs-modal-title">Uploaded Documents</h2>
                    <button className="uploaded-docs-modal-close" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                {/* Content - File List */}
                <div className="uploaded-docs-modal-content">
                    {loading ? (
                        <>
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                        </>
                    ) : attachments.length === 0 && uploadingFiles.length === 0 ? (
                        <div className="uploaded-docs-empty">
                            No documents uploaded yet
                        </div>
                    ) : (
                        <>
                            {uploadingFiles.map((file) => (
                                <div key={file.id} className="uploaded-doc-item uploading">
                                    <div className="uploaded-doc-item-main">
                                        <div className="uploaded-doc-info">
                                            <FileIconBadge ext={getFileExtension(file.file_name)} />
                                            <div className="uploaded-doc-details">
                                                <span className="uploaded-doc-name">{file.file_name}</span>
                                                <span className="upload-progress-text">Uploading...</span>
                                            </div>
                                        </div>
                                        <div className="uploaded-doc-actions">
                                            <div className="spinner-mini"></div>
                                        </div>
                                    </div>
                                    <div className="upload-progress-row">
                                        <div className="upload-progress-fill"></div>
                                    </div>
                                </div>
                            ))}
                            {attachments.map((attachment) => (
                                <div key={attachment.id} className="uploaded-doc-item">
                                    <div className="uploaded-doc-item-main">
                                        <div className="uploaded-doc-info">
                                            <FileIconBadge ext={getFileExtension(attachment.upload?.original_filename || attachment.file_name)} />
                                            <div className="uploaded-doc-details">
                                                <span className="uploaded-doc-name">
                                                    {attachment.upload?.original_filename || attachment.file_name || attachment.name || 'Document File'}
                                                </span>
                                                <span className="uploaded-doc-date">
                                                    {formatDate(attachment.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="uploaded-doc-actions">
                                            <button
                                                className="uploaded-doc-action-btn"
                                                onClick={() => {
                                                    const u = attachment.upload || {};
                                                    if (isImageFile(u)) setImageAttachment(attachment);
                                                    else if (isPdfFile(u)) setPdfAttachment(attachment);
                                                    else if (isOfficeFile(u)) setOfficeAttachment(attachment);
                                                    else onView?.(attachment);
                                                }}
                                                title="View"
                                            >
                                                <img src={viewAssetIcon} alt="View" className="doc-action-icon" />
                                            </button>
                                            <button
                                                className="uploaded-doc-action-btn"
                                                onClick={() => onDownload?.(attachment)}
                                                title="Download"
                                            >
                                                <img src={downloadAssetIcon} alt="Download" className="doc-action-icon" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="uploaded-docs-modal-footer">
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <button
                        className="upload-doc-btn"
                        onClick={handleUploadClick}
                    >
                        <img src={fileIcon} className="upload-doc-icon" alt="" />
                        <span>Upload Document</span>
                    </button>
                    <button
                        className="download-all-btn"
                        onClick={async () => {
                            try {
                                setIsDownloadingAll(true);
                                if (onDownloadAll) {
                                    await onDownloadAll();
                                }
                            } finally {
                                setIsDownloadingAll(false);
                            }
                        }}
                        disabled={attachments.length === 0 || isDownloadingAll}
                    >
                        {isDownloadingAll ? (
                            <div className="upload-loading-container">
                                <div className="spinner-mini" style={{ borderTopColor: '#ffffff' }}></div>
                                <span>Preparing ZIP...</span>
                            </div>
                        ) : (
                            <>
                                <img src={downloadIcon} className="download-all-icon" alt="" />
                                <span>Download All</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>

        {pdfAttachment && (
            <TaskAttachmentPdfViewerModal
                taskId={task.id}
                attachment={pdfAttachment}
                onClose={() => setPdfAttachment(null)}
            />
        )}
        {imageAttachment && (
            <TaskAttachmentImageViewerModal
                taskId={task.id}
                attachment={imageAttachment}
                allImageAttachments={imageAttachments}
                onClose={() => setImageAttachment(null)}
            />
        )}
        {officeAttachment && (
            <TaskAttachmentOnlyOfficeViewerModal
                taskId={task.id}
                attachment={officeAttachment}
                onClose={() => setOfficeAttachment(null)}
            />
        )}
        </>
    );
};

export default UploadedDocumentsModal;
